import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getProjectProductsApiV1ProductsOrgIdListGet } from '@/common/api/client/sdk.gen'
import type {
    GetProjectProductsApiV1ProductsOrgIdListGetData,
    MarketplaceType,
    PageProductListResponseSchema,
    ProductSource,
} from '@/common/api/client/types.gen'
import { useCurrentOrg } from '@/app/entities/org/model'
import type { PageResponse } from '@/common/hooks/useInfiniteScrollTable'
import { useInfiniteScrollTable } from '@/common/hooks/useInfiniteScrollTable'

const parseTabFromUrl = (searchParams: URLSearchParams, param: string, defaultTab: number): number => {
    const value = searchParams.get(param)
    if (!value) return defaultTab
    const tab = parseInt(value, 10)
    return Number.isFinite(tab) && tab >= 0 ? tab : defaultTab
}

const DEFAULT_PAGE_SIZE = 20

type ProductsListQuery = NonNullable<GetProjectProductsApiV1ProductsOrgIdListGetData['query']>
type ProductItem = PageProductListResponseSchema['items'][number]

export type ProductsListUrlFilters = {
    q: string
    /** Значение из `useUrlListFilters` с нужным allowlist (совместимо с API маркетплейса). */
    marketplace: string | undefined
    order_by: NonNullable<ProductsListQuery['order_by']>
    direction: 'asc' | 'desc'
    /** `score` из URL; передаётся в API при наличии. */
    score?: number | null
    /** Если задано в URL — перекрывает `source` из вкладки `tab`. */
    sourceFilter?: ProductSource | null
    /** Id бренда из URL; передаётся в API при наличии. */
    brand?: string | null
    /** Id продавца из URL; передаётся в API при наличии. */
    seller?: string | null
}

const getProducts = async (org_id: string, query: ProductsListQuery): Promise<PageProductListResponseSchema> => {
    const res = await getProjectProductsApiV1ProductsOrgIdListGet({
        path: { org_id },
        query,
    })
    return res.data!
}

const toPageResponse = (data: PageProductListResponseSchema): PageResponse<ProductItem> => ({
    items: data.items,
    total: data.total ?? undefined,
    pages: data.pages ?? undefined,
})

const fetchAllSelectableProductIds = async (
    org_id: string,
    params: Omit<ProductsListQuery, 'page'>
): Promise<string[]> => {
    const first = await getProducts(org_id, { ...params, page: 1 })
    const totalPages = first.pages ?? 1
    const allItems = [...(first.items ?? [])]

    if (totalPages > 1) {
        const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => getProducts(org_id, { ...params, page: i + 2 }))
        )
        for (const page of rest) {
            allItems.push(...(page.items ?? []))
        }
    }

    return allItems.filter((p): p is NonNullable<typeof p> => p != null && p.generation_score != null).map(p => p.id)
}

export const tabToConfig = (tab: number): Partial<ProductsListQuery> => {
    if (tab === 1) return { source: 'created' }
    if (tab === 2) return { source: 'organization' }
    if (tab === 3) return { source: 'competition' }
    return {}
}

export const useProductsInfinite = (source: string | undefined, listFilters: ProductsListUrlFilters) => {
    const { data: currentOrg } = useCurrentOrg()
    const org_id = currentOrg?.id
    const { q, marketplace, order_by, direction, score, sourceFilter, brand, seller } = listFilters
    const [searchParams] = useSearchParams()
    const defaultTab = source === 'generations' ? 3 : 0
    const tabFromUrlForQueryKey = parseTabFromUrl(searchParams, 'tab', defaultTab)
    const sourceFilterForQueryKey = useMemo(
        () => (tabFromUrlForQueryKey === 0 ? (sourceFilter ?? '') : ''),
        [tabFromUrlForQueryKey, sourceFilter]
    )

    const buildListParams = useCallback(
        (tab: number): Omit<ProductsListQuery, 'page'> => {
            const config = tabToConfig(tab)
            const params: Omit<ProductsListQuery, 'page'> = {
                size: DEFAULT_PAGE_SIZE,
                order_by,
                direction,
            }
            if (tab === 0 && sourceFilter) {
                params.source = sourceFilter
            } else {
                Object.assign(params, config)
            }
            if (q) params.q = q
            if (marketplace) params.marketplace = marketplace as MarketplaceType
            if (score != null && !Number.isNaN(Number(score))) params.score = Number(score)
            if (brand) params.brand = brand
            if (seller) params.seller = seller
            return params
        },
        [q, marketplace, order_by, direction, score, sourceFilter, brand, seller]
    )

    const mutateSearchParamsOnTabChange = useCallback((next: URLSearchParams, tabIndex: number) => {
        if (tabIndex !== 0) next.delete('source')
    }, [])

    const result = useInfiniteScrollTable<ProductItem>({
        queryKey: [
            'products-infinite',
            org_id,
            q,
            marketplace ?? '',
            order_by,
            direction,
            score ?? '',
            sourceFilterForQueryKey,
            brand ?? '',
            seller ?? '',
        ],
        fetchPage: async (page, ctx) => {
            const tab = ctx?.tabFromUrl ?? (source === 'generations' ? 3 : 0)
            const params: ProductsListQuery = {
                ...buildListParams(tab),
                page,
            }
            const data = await getProducts(org_id as string, params)
            return toPageResponse(data)
        },
        pageSize: DEFAULT_PAGE_SIZE,
        enabled: !!org_id,
        scrollParam: 'scroll',
        tabParam: 'tab',
        defaultTab,
        mutateSearchParamsOnTabChange,
    })

    const fetchAllSelectableIds = useCallback(async (): Promise<string[]> => {
        if (!org_id) return []
        const tab = result.tabFromUrl
        return fetchAllSelectableProductIds(org_id, buildListParams(tab))
    }, [org_id, result.tabFromUrl, buildListParams])

    return {
        ...result,
        fetchAllSelectableIds,
        data: {
            items: result.allItems.filter((i): i is ProductItem => i != null),
            pages: result.totalPages,
        },
        hasNextPage: result.requiredPages.length < result.totalPages,
        isFetchingNextPage: result.isFetching,
    }
}
